import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Loader2 } from 'lucide-react';
import { useMobileDetection } from '../../hooks/useMobileDetection';
import { MobileRestrictionMessage } from '@/components/ui/MobileRestrictionMessage';

function RequestGroupNumber({ text, closePopup }) {
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [prefixList, setPrefixList] = useState([]);
    const [selectedPrefix, setSelectedPrefix] = useState('');
    const [addedGroups, setAddedGroups] = useState([]);
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const isMobile = useMobileDetection();

    const userdata = useMemo(() => {
        try {
            return JSON.parse(localStorage.getItem('user'));
        } catch {
            return null;
        }
    }, []);

    const handleResponse = useCallback(async (response) => {
        try {
            const text = await response.text();
            return text ? JSON.parse(text) : [];
        } catch {
            return [];
        }
    }, []);

    const fetchPrefixList = useCallback(async () => {
        if (!userdata) {
            setLoading(false);
            return;
        }

        try {
            const response = await fetch('/api/ws/GroupNumber_Prefix', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Browser-Url': window.location.href
                },
                body: JSON.stringify({
                    Prefix: 'N/A',
                    WhName: userdata.wholesaler
                })
            });

            const apidata = await handleResponse(response);
            if (apidata?.length > 0) {
                setPrefixList(apidata);
                setSelectedPrefix(apidata[0].value);
            }
        } catch (err) {
        } finally {
            setLoading(false);
        }
    }, [userdata, handleResponse]);

    useEffect(() => {
        fetchPrefixList();
    }, [fetchPrefixList]);

    const handleSubmit = useCallback(async () => {
        if (!selectedPrefix) {
            setError('Please select a prefix');
            return;
        }

        setSubmitting(true);
        setError('');
        setSuccessMessage('');
        setAddedGroups([]);

        try {
            const response = await fetch('/api/ws/RequestGroupNumber', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    Prefix: selectedPrefix,
                    WhName: userdata?.wholesaler
                })
            });

            const apidata = await handleResponse(response);
            
            if (apidata?.length > 0) {
                if (apidata[0].code !== "-1") {
                    if (apidata[0].groups?.length > 0) {
                        setSuccessMessage(apidata[0].message);
                        setAddedGroups(apidata[0].groups);
                    } else {
                        setError(apidata[0].message);
                    }
                } else {
                    setError(apidata[0].message);
                }
            } else {
                setError('Error. Please try later.');
            }
        } catch (err) {
            setError('Error. Please try later.');
        } finally {
            setSubmitting(false);
        }
    }, [selectedPrefix, userdata, handleResponse]);

    const handleOpenChange = useCallback((open) => {
        if (!open) {
            closePopup?.();
        }
    }, [closePopup]);

    return (
        <Dialog open={true} onOpenChange={handleOpenChange}>
            <DialogContent className="sm:max-w-md">
                <DialogHeader>
                    <DialogTitle>{text}</DialogTitle>
                </DialogHeader>

                <div className="space-y-4 py-4">
                    {loading ? (
                        <div className="flex items-center justify-center py-8">
                            <Loader2 className="h-8 w-8 animate-spin text-primary" />
                        </div>
                    ) : isMobile ? (
                        <MobileRestrictionMessage />
                    ) : (
                        <>
                            {prefixList.length > 1 && (
                                <div className="space-y-2">
                                    <Label htmlFor="prefix">Select Prefix</Label>
                                    <Select value={selectedPrefix} onValueChange={setSelectedPrefix}>
                                        <SelectTrigger id="prefix">
                                            <SelectValue placeholder="Select prefix" />
                                        </SelectTrigger>
                                        <SelectContent className="max-h-[200px] overflow-y-auto">
                                            {prefixList.map((item, index) => (
                                                <SelectItem key={index} value={item.value}>
                                                    {item.value}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            )}

                            {prefixList.length === 1 && (
                                <div className="space-y-2">
                                    <Label>Prefix</Label>
                                    <p className="text-sm text-muted-foreground">{selectedPrefix}</p>
                                </div>
                            )}

                            {/* Error Message */}
                            {error && (
                                <p className="text-sm text-destructive">{error}</p>
                            )}

                            {/* Success Message */}
                            {successMessage && (
                                <p className="text-sm text-green-600">{successMessage}</p>
                            )}

                            {/* Added Groups */}
                            {addedGroups.length > 0 && (
                                <div className="space-y-2">
                                    <Label>Created Group Numbers</Label>
                                    <div className="flex flex-wrap gap-2">
                                        {addedGroups.map((group, index) => (
                                            <Badge key={index} variant="secondary">
                                                {group.groupNumber}
                                            </Badge>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>

                <DialogFooter>
                    <Button onClick={handleSubmit} disabled={loading || submitting}>
                        {submitting ? (
                            <>
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                Creating...
                            </>
                        ) : (
                            'Create'
                        )}
                    </Button>
                    <Button variant="secondary" onClick={closePopup}>
                        Cancel
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default RequestGroupNumber;
